// ===================================
// KAIXO - StoriesViewer (fullscreen autoavance)
// ===================================
// Igual que Instagram Stories: una historia a la vez, barra superior con
// segmentos de progreso, tap derecha = siguiente / izquierda = anterior,
// al terminar cada historia pasa a la siguiente del mismo canal, al
// terminar el canal salta al siguiente. Videos reproducen 1x, fotos
// duran 5s por defecto.
// ===================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../Icons/Icons';
import { optimizedVideoUrl, optimizedImageUrl } from '../../utils/cloudinaryUrl';
import './StoriesViewer.css';

const PHOTO_DURATION_MS = 5000;

export default function StoriesViewer({ groups, startGroupIdx = 0, onClose }) {
    const [groupIdx, setGroupIdx] = useState(startGroupIdx);
    const [storyIdx, setStoryIdx] = useState(0);
    const [slideIdx, setSlideIdx] = useState(0);  // dentro de un carrusel multi-media
    const [progress, setProgress] = useState(0);  // 0..1 de la historia actual
    const [paused, setPaused] = useState(false);
    // Flag por slide: si es video, solo arrancamos el timer cuando el
    // video emite 'playing' (evita que la barra avance mientras el video
    // aún está cargando y se ve la mitad).
    const [mediaReady, setMediaReady] = useState(false);
    const rafRef = useRef(null);
    const startTsRef = useRef(0);
    const videoRef = useRef(null);

    const group = groups[groupIdx];
    const stories = group?.stories || [];
    const story = stories[storyIdx];
    const media = (story?.mediaUrls && story.mediaUrls.length)
        ? story.mediaUrls
        : (story?.mediaUrl ? [{ url: story.mediaUrl, type: story.mediaType === 'video' ? 'video' : 'image' }] : []);
    const currentMedia = media[slideIdx];

    // Navegación
    const goNextInternal = useCallback(() => {
        if (!story) return;
        if (slideIdx + 1 < media.length) {
            setSlideIdx(s => s + 1);
            return;
        }
        if (storyIdx + 1 < stories.length) {
            setStoryIdx(s => s + 1);
            setSlideIdx(0);
            return;
        }
        // Siguiente canal
        if (groupIdx + 1 < groups.length) {
            setGroupIdx(g => g + 1);
            setStoryIdx(0);
            setSlideIdx(0);
            return;
        }
        onClose && onClose();
    }, [slideIdx, media.length, storyIdx, stories.length, groupIdx, groups.length, onClose, story]);

    const goPrevInternal = useCallback(() => {
        if (slideIdx > 0) { setSlideIdx(s => s - 1); return; }
        if (storyIdx > 0) {
            setStoryIdx(s => s - 1);
            setSlideIdx(0);
            return;
        }
        if (groupIdx > 0) {
            const prevGroup = groups[groupIdx - 1];
            setGroupIdx(g => g - 1);
            setStoryIdx(Math.max(0, (prevGroup?.stories?.length || 1) - 1));
            setSlideIdx(0);
            return;
        }
    }, [slideIdx, storyIdx, groupIdx, groups]);

    // Reset del flag de listo cada vez que cambiamos de slide/story/group.
    useEffect(() => {
        setProgress(0);
        setMediaReady(false);
    }, [groupIdx, storyIdx, slideIdx]);

    // Timer RAF SOLO para FOTOS (5s fijos). Los videos NO usan este timer:
    // su progreso se calcula con onTimeUpdate sobre currentTime/duration,
    // así la barra refleja el estado real del video (no una estimación).
    useEffect(() => {
        if (!currentMedia) return;
        if (currentMedia.type !== 'image') return;
        if (paused) return;
        if (!mediaReady) return;

        startTsRef.current = performance.now() - (progress * PHOTO_DURATION_MS);
        const tick = (ts) => {
            const elapsed = ts - startTsRef.current;
            const p = Math.min(1, elapsed / PHOTO_DURATION_MS);
            setProgress(p);
            if (p >= 1) {
                goNextInternal();
                return;
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaReady, paused, currentMedia, goNextInternal]);

    // Video: reproducir/pausar según flag
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (paused) v.pause();
        else v.play().catch(() => {});
    }, [paused, currentMedia]);

    if (!story || !currentMedia) return null;

    const onTapSide = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        if (x < rect.width * 0.35) goPrevInternal();
        else goNextInternal();
    };

    return ReactDOM.createPortal(
        <div className="sv-overlay"
            onPointerDown={() => setPaused(true)}
            onPointerUp={() => setPaused(false)}
            onPointerCancel={() => setPaused(false)}
            onPointerLeave={() => setPaused(false)}
        >
            {/* Barra superior de segmentos de progreso */}
            <div className="sv-progress">
                {stories.map((s, i) => {
                    const totalSlides = (s.mediaUrls?.length || 1);
                    return Array.from({ length: totalSlides }).map((_, si) => {
                        let fill = 0;
                        if (i < storyIdx) fill = 1;
                        else if (i === storyIdx) {
                            if (si < slideIdx) fill = 1;
                            else if (si === slideIdx) fill = progress;
                        }
                        return (
                            <div className="sv-progress-seg" key={`${i}-${si}`}>
                                <div className="sv-progress-fill" style={{ width: `${fill * 100}%` }} />
                            </div>
                        );
                    });
                })}
            </div>

            {/* Header con avatar del canal y nombre — abre el perfil al tocar */}
            <div className="sv-header">
                <div
                    className="sv-owner sv-owner-clickable"
                    onClick={(e) => {
                        e.stopPropagation();
                        const code = group.channel.inviteCode
                            || (group.channel.handle ? `@${group.channel.handle}` : null);
                        if (!code) return;
                        try { onClose && onClose(); } catch {}
                        window.dispatchEvent(new CustomEvent('open_channel_profile', {
                            detail: { inviteCode: code }
                        }));
                    }}
                >
                    <div className="sv-owner-avatar">
                        {group.channel.avatar
                            ? <img src={group.channel.avatar} alt="" />
                            : (group.channel.name?.[0] || '?').toUpperCase()}
                    </div>
                    <span className="sv-owner-name">{group.channel.name}</span>
                </div>
                <button className="sv-close" onClick={onClose} aria-label="Cerrar">
                    <Icons.Close size={22} color="white" />
                </button>
            </div>

            {/* Área central tap-to-navigate */}
            <div className="sv-stage" onClick={onTapSide}>
                {currentMedia.type === 'video' ? (
                    <video
                        key={story._id + '-' + slideIdx}
                        ref={videoRef}
                        src={optimizedVideoUrl(currentMedia.url)}
                        className="sv-media"
                        playsInline
                        autoPlay
                        muted
                        preload="auto"
                        onLoadedMetadata={() => setMediaReady(true)}
                        onTimeUpdate={(e) => {
                            // La barra de progreso sigue el tiempo REAL del video.
                            // Si el video está buffering, currentTime no avanza,
                            // así que la barra tampoco avanza. Cuando sigue cargando,
                            // todo se mantiene sincronizado.
                            const v = e.currentTarget;
                            if (v.duration && !Number.isNaN(v.duration) && v.duration > 0) {
                                setProgress(Math.min(1, v.currentTime / v.duration));
                            }
                        }}
                        onEnded={goNextInternal}
                    />
                ) : (
                    <img
                        key={story._id + '-' + slideIdx}
                        src={optimizedImageUrl(currentMedia.url)}
                        alt=""
                        className="sv-media"
                        onLoad={() => setMediaReady(true)}
                    />
                )}
            </div>

            {/* Caption opcional */}
            {(story.text || story.originalText) && (
                <div className="sv-caption">
                    {story.text || story.originalText}
                </div>
            )}
        </div>,
        document.body
    );
}
